import React, { useState } from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { formatDateTime } from '@utils/dateTime';
import {
  Button,
  Tag,
  message,
  Space,
  Descriptions,
  Modal,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLoaderData } from 'react-router-dom';

export default function SupplierShow({
  service,
  role = 'admin',
  breadcrumbPrefix = ['purchase_orders', 'purchase_orders.suppliers'],
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const supplier = useLoaderData();
  const [deleting, setDeleting] = useState(false);

  if (!supplier) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <p>{t('common.error.not_found')}</p>
      </div>
    );
  }

  const handleDelete = () => {
    Modal.confirm({
      title: t('common.confirm.delete'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm.delete_description'),
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        setDeleting(true);
        try {
          const response = await service.destroy(supplier.id);
          if (response.success) {
            message.success(t('common.message.delete_success'));
            navigate('/suppliers');
          } else {
            handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
          }
        } catch (error) {
          handleNetworkError(error, { t });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  return (
    <div>
      <ListPageHeader
        basePath={`/${role}`}
        items={[...breadcrumbPrefix, { label: supplier.name }]}
        actions={
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
            >
              {t('common.button.edit')}
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleting}
            >
              {t('common.button.delete')}
            </Button>
          </Space>
        }
      />
      <Descriptions column={1} bordered>
        <Descriptions.Item label={t('models.supplier.name')}>{supplier.name}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.code')}>{supplier.code || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.contact_name')}>{supplier.contact_name || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.contact_email')}>{supplier.contact_email || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.contact_phone')}>{supplier.contact_phone || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.address')}>{supplier.address || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.payment_terms')}>{supplier.payment_terms || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.default_currency')}>{supplier.default_currency || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.tax_rate')}>{supplier.tax_rate != null ? `${(supplier.tax_rate * 100).toFixed(1)}%` : '-'}</Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.activated')}>
          <Tag color={supplier.activated ? 'green' : 'red'}>
            {supplier.activated ? t('common.status.active') : t('common.status.inactive')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('models.supplier.note')}>{supplier.note || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('common.created_at')}>{supplier.created_at ? formatDateTime(supplier.created_at) : '-'}</Descriptions.Item>
        <Descriptions.Item label={t('common.updated_at')}>{supplier.updated_at ? formatDateTime(supplier.updated_at) : '-'}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}
